import { useEffect, useMemo, useState } from 'react';
import { Button } from '@interstate/components/Button';
import { PlusSmallIcon } from '@interstate/components/Icons/PlusSmallIcon';
import { Typography } from '@interstate/components/Typography';
import { useCustomerCardContext } from '../../CustomerFormContext';
import { AddressList, AddressCard } from './address.styles';
import { hasAddressErrors } from './address.utils';
import AddressHeader from './AddressHeader';
import AddressSummary from './AddressSummary';
import AddressFields from './AddressFields';
import BuyerAddressCopy from './BuyerAddressCopy';
import { CUSTOMER_CARD_ANALYTICS } from '../../../analytics';

/**
 * Top-level address section. Orchestrates primary and billing address cards,
 * mode switching (view ↔ edit), and the "Add Billing Address" action.
 * For co-buyer forms, also renders the BuyerAddressCopy checkbox row.
 */
const CustomerAddresses = () => {
    const { formValues, formErrors, handleInitBillingAddress, customerRole, isLocked, onAnalyticsEvent } = useCustomerCardContext();
    const [isPrimaryEditing, setIsPrimaryEditing] = useState(false);
    const [isBillingEditing, setIsBillingEditing] = useState(false);

    const hasBillingAddress = Boolean(formValues.billingAddress);

    const hasPrimaryAddressFieldErrors = useMemo(() => hasAddressErrors(formErrors), [formErrors]);

    // Open the primary address whenever it has a field error, so a blocking rule (e.g. a required
    // Postal Code that the deal has no value for) is visible and fixable instead of hidden behind the
    // collapsed read-only summary. Subsumes the Generate-Contract flow, whose validateAll simply
    // produces the errors that open it.
    useEffect(() => {
        if (hasPrimaryAddressFieldErrors) {
            setIsPrimaryEditing(true);
        }
    }, [hasPrimaryAddressFieldErrors]);

    const handleAddBilling = () => {
        onAnalyticsEvent?.(CUSTOMER_CARD_ANALYTICS.CLICKED_ADD_BILLING_ADDRESS, { customerRole });
        handleInitBillingAddress();
        setIsBillingEditing(true);
    };

    return (
        <>
            <Typography variant="h5" sx={{ marginBottom: '1rem', marginTop: '1.5rem' }}>
                Addresses
            </Typography>
            {customerRole === 'coBuyer' && (
                <BuyerAddressCopy setIsPrimaryEditing={setIsPrimaryEditing} setIsBillingEditing={setIsBillingEditing} />
            )}
            <AddressList>
                <AddressCard
                    $editing={isPrimaryEditing}
                    header={
                        <AddressHeader
                            type="primary"
                            hasBillingAddress={hasBillingAddress}
                            isEditing={isPrimaryEditing}
                            setIsPrimaryEditing={setIsPrimaryEditing}
                            setIsBillingEditing={setIsBillingEditing}
                        />
                    }
                    content={isPrimaryEditing ? <AddressFields type="primary" /> : <AddressSummary type="primary" />}
                />
                {hasBillingAddress ? (
                    <AddressCard
                        $editing={isBillingEditing}
                        header={
                            <AddressHeader
                                type="billing"
                                hasBillingAddress={hasBillingAddress}
                                isEditing={isBillingEditing}
                                setIsPrimaryEditing={setIsPrimaryEditing}
                                setIsBillingEditing={setIsBillingEditing}
                            />
                        }
                        content={isBillingEditing ? <AddressFields type="billing" /> : <AddressSummary type="billing" />}
                    />
                ) : (
                    <Button
                        buttonStyle="secondary"
                        size="small"
                        onClick={handleAddBilling}
                        disabled={isLocked}
                        data-testid="add-billing-address-button"
                        startIcon={<PlusSmallIcon />}
                        sx={{ marginTop: '0.5rem', width: '100%' }}
                    >
                        Add Billing Address
                    </Button>
                )}
            </AddressList>
        </>
    );
};

export default CustomerAddresses;
